import { line } from '@visx/shape';
import { scaleLinear } from '@visx/scale';
import { useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { valueDomain, type IndexRange } from './math';
import { useElementSize } from './use-element-size';

export interface BrushProps {
  /** Number of steps on the axis. */
  length: number;
  /** Overview series drawn behind the window. */
  values: ArrayLike<number>;
  color: string;
  range: IndexRange;
  onChange: (range: IndexRange) => void;
  /** Accessible names for the two handles and a formatter for their values. */
  startLabel: string;
  endLabel: string;
  formatIndex: (index: number) => string;
  height?: number;
  /** Smallest window, in steps. */
  minSpan?: number;
}

type Drag = { kind: 'move' | 'start' | 'end'; originX: number; origin: IndexRange } | null;

/**
 * Time-range brush: a mini overview with a draggable window and two keyboard-accessible handles
 * (role="slider"; ←/→ move a month, ⇧ a year, Home/End).
 */
export function Brush({
  length,
  values,
  color,
  range,
  onChange,
  startLabel,
  endLabel,
  formatIndex,
  height = 44,
  minSpan = 12,
}: BrushProps) {
  const [ref, { width }] = useElementSize<HTMLDivElement>();
  const [drag, setDrag] = useState<Drag>(null);
  const last = Math.max(0, length - 1);
  const x = useMemo(
    () => scaleLinear<number>({ domain: [0, last], range: [0, width] }),
    [last, width],
  );
  const y = useMemo(
    () => scaleLinear<number>({ domain: valueDomain([values]), range: [height - 4, 4] }),
    [values, height],
  );
  const path = useMemo(() => {
    const indices = Array.from({ length }, (_, i) => i);
    return line<number>({ x: (i) => x(i), y: (i) => y(values[i] ?? 0) })(indices) ?? '';
  }, [length, values, x, y]);

  const clamp = ([a, b]: IndexRange): IndexRange => {
    const start = Math.max(0, Math.min(a, last - minSpan));
    const end = Math.min(last, Math.max(b, start + minSpan));
    return [start, end];
  };

  const toIndex = (target: Element, clientX: number): number => {
    const svg = target instanceof SVGSVGElement ? target : target.closest('svg');
    const rect = svg?.getBoundingClientRect();
    if (!rect || width === 0) return 0;
    return Math.round(x.invert(clientX - rect.left));
  };

  const onPointerDown = (kind: 'move' | 'start' | 'end') => (e: PointerEvent<SVGElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ kind, originX: toIndex(e.currentTarget, e.clientX), origin: range });
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>): void => {
    if (!drag) return;
    const delta = toIndex(e.currentTarget, e.clientX) - drag.originX;
    const [a, b] = drag.origin;
    if (drag.kind === 'move') {
      const span = b - a;
      const start = Math.max(0, Math.min(last - span, a + delta));
      onChange([start, start + span]);
    } else if (drag.kind === 'start') onChange(clamp([Math.min(a + delta, b - minSpan), b]));
    else onChange(clamp([a, Math.max(b + delta, a + minSpan)]));
  };

  const onHandleKey = (which: 'start' | 'end') => (e: KeyboardEvent<SVGElement>) => {
    const step = e.shiftKey ? 12 : 1;
    const [a, b] = range;
    let next: IndexRange | null = null;
    const v = which === 'start' ? a : b;
    const set = (value: number): IndexRange =>
      which === 'start'
        ? clamp([Math.min(value, b - minSpan), b])
        : clamp([a, Math.max(value, a + minSpan)]);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = set(v - step);
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = set(v + step);
    else if (e.key === 'Home') next = set(which === 'start' ? 0 : a + minSpan);
    else if (e.key === 'End') next = set(which === 'start' ? b - minSpan : last);
    if (next) {
      e.preventDefault();
      onChange(next);
    }
  };

  const x0 = x(range[0]);
  const x1 = x(range[1]);

  return (
    <div ref={ref} className="w-full select-none" style={{ height }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          onPointerMove={onPointerMove}
          onPointerUp={() => {
            setDrag(null);
          }}
          className="overflow-visible"
        >
          <rect width={width} height={height} rx={6} style={{ fill: 'var(--bg-2)' }} />
          <path d={path} fill="none" style={{ stroke: color }} strokeWidth={1.25} opacity={0.7} />
          <rect
            x={0}
            width={x0}
            height={height}
            rx={6}
            style={{ fill: 'var(--bg-0)' }}
            opacity={0.6}
          />
          <rect
            x={x1}
            width={Math.max(0, width - x1)}
            height={height}
            rx={6}
            style={{ fill: 'var(--bg-0)' }}
            opacity={0.6}
          />
          <rect
            x={x0}
            width={Math.max(0, x1 - x0)}
            height={height}
            rx={6}
            fill="transparent"
            style={{ stroke: 'var(--accent)', cursor: drag?.kind === 'move' ? 'grabbing' : 'grab' }}
            strokeWidth={1.5}
            onPointerDown={onPointerDown('move')}
          />
          {(['start', 'end'] as const).map((which) => {
            const hx = which === 'start' ? x0 : x1;
            const value = which === 'start' ? range[0] : range[1];
            return (
              <g
                key={which}
                role="slider"
                tabIndex={0}
                aria-label={which === 'start' ? startLabel : endLabel}
                aria-valuemin={0}
                aria-valuemax={last}
                aria-valuenow={value}
                aria-valuetext={formatIndex(value)}
                onKeyDown={onHandleKey(which)}
                onPointerDown={onPointerDown(which)}
                style={{ cursor: 'ew-resize', outline: 'none' }}
                className="group"
              >
                <rect x={hx - 6} y={0} width={12} height={height} fill="transparent" />
                <rect
                  x={hx - 2.5}
                  y={height / 2 - 9}
                  width={5}
                  height={18}
                  rx={2.5}
                  style={{ fill: 'var(--accent)' }}
                  className="group-focus-visible:[stroke:var(--focus-ring)] group-focus-visible:[stroke-width:3px]"
                />
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
